import React, { useState, useEffect } from "react";
import {
    Box,
    Dialog,
    DialogContent,
    IconButton,
    DialogActions,
    Button,
    List,
    ListItem,
    Paper,
    ListItemText,
    TextField,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import CircularProgress from '@mui/material/CircularProgress';
import SendIcon from '@mui/icons-material/Send';
import { GetClinicalNotes } from "../services";

function DataAIModal({ openModal, handleCloseModal, db, sql }) {
    const [loading, setLoading] = useState(false);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("lg"));
    const [isTyping, setIsTyping] = useState(false);
    const [message, setMessage] = useState('');
    const [conversation, setConversation] = useState([]);
    const [data, setData] = useState([]);
    const [headers, setHeaders] = useState([]);

    useEffect(() => {
        if (openModal) {
            const init = {
                body: {
                    db: db,
                    filterText: sql,
                    sampleSize: 25
                }
            };
            setLoading(true);
            GetClinicalNotes(init).then((res) => {
                if (res.success) {
                    const parsedData = JSON.parse(res.data)?.data;
                    const headersFromFirstRecord = parsedData.length > 0 ? Object.keys(parsedData[0]) : [];
                    setData(parsedData);
                    setHeaders(headersFromFirstRecord);
                    setConversation([{ text: "Hello, welcome to RWEdataLab. How can I help you?", sender: 'system' }]);
                }

                setLoading(false);
            })
                .catch(err => {
                    console.log({ err });
                    setLoading(false);
                })
        }
        return () => {
            setConversation([]);
        }
    }, [openModal, db, sql]);

    const formattedHeaders = () => {
        return headers.map(header => `${header.replace(/_/g, ' ')}`).join(', ');
    }

    const sendToOpenAI = async (userMessage) => {
        setIsTyping(true);

        const conversationHistory = conversation.map(c => ({ role: c.sender, content: c.text }));

        const formattedData = JSON.stringify(data, null, 2);

        const initialContext = {
            role: 'system',
            content: `You are an AI assistant tasked with querying patient records. Your responses must strictly follow this JSON structure:
            {
              "description": "DESCRIPTION_PLACEHOLDER",
              "count": COUNT_PLACEHOLDER,
              "patients": [
                {
                  "PATIENT_KEY": "KEY_PLACEHOLDER",
                  // Include additional fields as required
                }
                // Additional patient objects
              ]
            }

            Remember to replace placeholders with actual data based on user queries. Respond in a brief, conversational tone and ensure accuracy in patient data retrieval and reporting.
            
            -- Example Questions and Expected Answers:
            Question: How many patients with anxiety?
            Answer:
            { 
                "description": "I found 2 patients with anxiety", 
                "count": 2, 
                "patients": [ 
                    { 
                        "PATIENT_KEY": "d446b2cf99538137740457a834f716fb" 
                        // Include additional fields as required
                    }, 
                    { 
                        "PATIENT_KEY": "b7ff8cd0c328a1244458aa2b1ae68a5f" 
                        // Include additional fields as required
                    } 
                ] 
            }

            Question: How many patients were suicidal?
            Answer:
            {
                "description": "I found 1 patient that was suicidal",
                "count": 1,
                "patients": [
                  {
                    "PATIENT_KEY": "5c67266c3b9b660c22ccd37e957de0c6",
                    "HISTORY_OF_PRESENT_ILLNESS": "1. schizophrenia doing well. pt needs medications to keep controlled. 2. anxiety, doing well, needs medications to help 3. neck pain intermittent with the se of medications. doing well with norco. 4. acne, moderate constant, non radiating to face only. wants to try medications to help.",
                    "PAST_MEDICAL_HISTORY": "schizophrenia, Anxiety"
                    // Include additional fields as required
                  },
                  // Additional patient objects
                ]
              }

            \n\n${formattedData}\n
           `
        };


        try {
            const response = await axios.post(
                'https://api.openai.com/v1/chat/completions',
                {
                    model: 'gpt-4-turbo',
                    messages: [initialContext, ...conversationHistory, { role: "user", content: userMessage }],
                    temperature: 0
                },
                {
                    headers: {
                        'Authorization': `Bearer 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`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            const aiMessage = response.data.choices[0].message.content;
            // Only add the AI message to the conversation if it's not null or empty
            if (aiMessage && aiMessage.trim()) {
                setConversation([...conversation, { text: userMessage, sender: 'user' }, { text: aiMessage, sender: 'system' }]);
            } else {
                // Directly add the user's message to avoid the empty AI message bubble
                setConversation([...conversation, { text: userMessage, sender: 'user' }]);
            }
            setIsTyping(false);

        } catch (error) {
            console.error('Error calling OpenAI:', error);
            setIsTyping(false);
        }
    };

    const handleSendMessage = () => {
        if (message.trim()) {
            setConversation([...conversation, { text: message, sender: 'user' }]);
            sendToOpenAI(message);
            setMessage('');
        }
    };

    const DisplayData = ({ data }) => {
        const description = data?.description || 'No detailed description available.';
        const patients = data?.patients || [];
        const patientHeaders = patients.length > 0 ? Object.keys(patients[0]) : [];

        return (
            <Box>
                <Typography variant="body1">{description}</Typography>
                {patients.length > 0 && (
                    <TableContainer component={Paper} sx={{ mt: 2 }}>
                        <Table sx={{ minWidth: 650 }} aria-label="dynamic table">
                            <TableHead>
                                <TableRow>
                                    {patientHeaders.length > 0 ? patientHeaders.map(header => (
                                        <TableCell key={header}>{header.replace(/_/g, ' ').toUpperCase()}</TableCell>
                                    )) : <TableCell>Patient Key</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {patients.map((patient, index) => (
                                    <TableRow key={index}>
                                        {patientHeaders.length > 0 ? patientHeaders.map(header => (
                                            <TableCell key={`${index}-${header}`}>
                                                {patient[header]}
                                            </TableCell>
                                        )) : <TableCell>{patient.PATIENT_KEY}</TableCell>}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        );
    };

    const parseJsonResponse = (content) => {
        try {
            // First try to match embedded JSON format
            const jsonRegex = /```json\s+({[\s\S]*?})\s+```/;
            const match = content.match(jsonRegex);
            if (match && match[1]) {
                return JSON.parse(match[1].trim());
            } else {
                // Try to parse the content directly as JSON
                return JSON.parse(content.trim());
            }
        } catch (error) {
            console.error("Failed to parse JSON:", error);
        }
        return null;
    };



    return (
        <Dialog
            fullScreen={fullScreen}
            fullWidth
            maxWidth="lg"
            open={openModal}
            onClose={() => handleCloseModal()}
            aria-labelledby="ai-modal-title"
            aria-describedby="ai-modal-description"
        >
            <DialogTitle
                id="responsive-dialog-title"
                sx={{
                    borderBottom: "1px solid lightgrey",
                    typography: 'h6'
                }}
            >
                RWE AI
            </DialogTitle>
            <DialogContent sx={{ p: 0, minHeight: '600px' }}>
                {loading ?
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <CircularProgress />
                    </Box>
                    :
                    <Box sx={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
                        <List sx={{ overflowY: 'auto', flexGrow: 1, p: 2, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                            {conversation.map((msg, index) => {
                                const jsonData = parseJsonResponse(msg.text);
                                return (
                                    <ListItem key={index} sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                                        p: 0,
                                        animation: 'fadeIn 0.5s'
                                    }}>
                                        <Paper sx={{
                                            maxWidth: '80%',
                                            p: 1,
                                            bgcolor: msg.sender === 'user' ? '#caedf9' : '#f6f6f6',
                                            color: msg.sender === 'user' ? '#ffffff' : '#666262',
                                            mt: 1,
                                            mb: 1,
                                            borderRadius: '15px'
                                        }}>
                                            {jsonData ?
                                                <DisplayData key={index} data={jsonData} />
                                                :
                                                <ListItemText primary={msg.text} primaryTypographyProps={{ variant: 'body2' }} />
                                            }
                                        </Paper>
                                    </ListItem>
                                )
                            })}
                            {isTyping && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    <div className="typing-indicator">
                                        <div></div><div></div><div></div>
                                    </div>
                                </Box>
                            )}
                        </List>
                        <Box sx={{ display: 'flex', alignItems: 'center', borderTop: '1px solid lightgrey', p: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                                placeholder="Type your message here..."
                            />
                            <IconButton onClick={handleSendMessage} color="primary">
                                <SendIcon />
                            </IconButton>
                        </Box>
                    </Box>
                }
            </DialogContent>
            <DialogActions sx={{ borderTop: '1px solid lightgrey' }}>
                <Button
                    color="error"
                    sx={{ margin: "5px" }}
                    onClick={() => handleCloseModal()}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );


}

export default DataAIModal